import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
}

export const useAdminAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isAdmin: false,
    isLoading: true,
  });

  const checkAdminRole = async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .rpc('has_role', { _user_id: userId, _role: 'admin' });

      if (error) {
        console.error('Error checking admin role:', error);
        return false;
      }

      return data === true;
    } catch (error) {
      console.error('Error checking admin role:', error);
      return false;
    }
  };

  useEffect(() => {
    let mounted = true;

    // 1. Set up listener FIRST, but keep it synchronous.
    //    Defer any Supabase call (rpc/query) with setTimeout to avoid
    //    a deadlock with the auth client's internal lock.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;

        if (session?.user) {
          // Immediate sync update — no awaits here.
          // Keep isLoading true until role check resolves to avoid the
          // guard briefly seeing "authenticated but not admin" → redirect.
          setAuthState((prev) => ({
            ...prev,
            user: session.user,
            isAuthenticated: true,
          }));

          // Defer the role check to the next tick (avoids auth client deadlock)
          setTimeout(async () => {
            const hasRole = await checkAdminRole(session.user.id);
            if (!mounted) return;
            setAuthState((prev) => ({ ...prev, isAdmin: hasRole, isLoading: false }));
          }, 0);
        } else {
          setAuthState({
            user: null,
            isAuthenticated: false,
            isAdmin: false,
            isLoading: false,
          });
        }
      }
    );

    // 2. THEN check for an existing session.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;

      if (session?.user) {
        setAuthState((prev) => ({
          ...prev,
          user: session.user,
          isAuthenticated: true,
        }));

        setTimeout(async () => {
          const hasRole = await checkAdminRole(session.user.id);
          if (!mounted) return;
          setAuthState((prev) => ({ ...prev, isAdmin: hasRole, isLoading: false }));
        }, 0);
      } else {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isAdmin: false,
          isLoading: false,
        });
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('Login error:', error);
        return { success: false, error: error.message };
      }

      if (!data.user) {
        return { success: false, error: 'Erreur de connexion' };
      }

      const hasAdminRole = await checkAdminRole(data.user.id);

      if (!hasAdminRole) {
        await supabase.auth.signOut();
        return { success: false, error: "Accès non autorisé" };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Erreur de connexion' };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        isLoading: false,
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user) {
      const hasRole = await checkAdminRole(session.user.id);
      setAuthState(prev => ({ ...prev, isAdmin: hasRole }));
    }
  };

  return {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated && authState.isAdmin,
    isLoading: authState.isLoading,
    login,
    logout,
    checkSession
  };
};
